import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../core/redux/api/reduxStandardApiAction';
import { API_ENTITLEMENT_HOTLIST_REMOVE } from '../../core/redux/api/endpoints';
import { IAppState } from '../../core/redux/IAppState';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../core/redux/api/circuitBreaker/module';
import { EntitlementHotlist } from '../entitlements/models';
import { assoc } from '../../language/module';


const KEY_ACTION = ACTION_TYPE.REMOVE_FROM_ENTITLEMENT_HOTLIST_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.REMOVE_FROM_ENTITLEMENT_HOTLIST_REQUEST,
  ACTION_TYPE.REMOVE_FROM_ENTITLEMENT_HOTLIST_RESPONSE,
  ACTION_TYPE.REMOVE_FROM_ENTITLEMENT_HOTLIST_ERROR,
];


/**
 * Constructs an action that can be dispatched to remove a guest from the hotlist (blacklist the guest's passes)
 */
export const removeFromEntitlementHotlistAction =
  (entitlementId: string, guestId: string, resort: string, comments: string, entitlementHotlistId: number) =>
  makeApiAction({
    method:   'PUT',
    endpoint: state => API_ENTITLEMENT_HOTLIST_REMOVE(state.selectedResort.resortAbbreviation),
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    body:     state => JSON.stringify({
        userId:               state.userState.userId,
        resortAbbreviation:             state.selectedResort.resortAbbreviation,
        masterguestid:        guestId,
        entitlementhotlistid: entitlementHotlistId,
        commenttext:          comments,
        resort:               resort,
        modifiedbyid:         state.userState.userName + ' - ' + state.selectedResort.resortAbbreviation,
        modifiedByDescription: resort + ' - Central Resort Tools',
    }),
    types:    ACTIONS.map(action => ({
      type: action,
      meta: { entitlementId, entitlementHotlistId, comments, modifiedDate: new Date() },
    })) as RsaaTypes,
    bailout:  state => !!getLoading(state, guestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<undefined> = (state, action, error, isLoading, payload) => {
  const entitlementId = action.meta.entitlementId.toString();
  const entitlementHotlistId = action.meta.entitlementHotlistId;
  const modifiedDate = action.meta.modifiedDate;
  const unhotlistComment = action.meta.comments;

  if (isLoading) {
    return addLoading(state, entitlementHotlistId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, entitlementHotlistId, KEY_ACTION, error);
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, entitlementHotlistId!, KEY_ACTION) as IAppState;

  // Mark the current hotlist entry as 'Inactive' without hitting the remote API to refresh all hotlists
  const alteredHotlists =
    noLoadingOrError.entitlementHotlists.assocIn(entitlementId,
      (hotlists: EntitlementHotlist[]) => hotlists.map(
        (hotl: EntitlementHotlist) => hotl.entitlementHotlistId === entitlementHotlistId
          ? R.mergeDeepRight(hotl,
            {
              'status':           'Inactive',
              'modifiedDate':     modifiedDate,
              'unhotlistComment': unhotlistComment,
              'modifiedById':     state.userState.userName + ' - ' + state.selectedResort.resortAbbreviation,
            })
          : hotl)
    );

  return assoc('entitlementHotlists', alteredHotlists, noLoadingOrError);
};


export const removeFromEntitlementHotlistReducer = makeApiReducer<undefined>(ACTIONS, KEY_ACTION, reducer);
